import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { captureException } from '@sentry/react'
import Compressor from 'compressorjs'
import { SpinnerWrapper } from '../../../components/common/SpinnerWrapper'
import { Button, ButtonVariant } from '../../../components/common/Button'
import { theme } from '../../../theme/theme'
import { IllustrationChoices } from '../../../components/common/Illustration'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { ErrorView } from '../../error/ErrorView'
import { Todo } from '../../../components/Todo/Todo'
import { IconChoices } from '../../../components/common/Icon'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { isImageType } from '../../../helpers/types'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../../components/common/Toast/toast-type'
import { convertBase64 } from '../../../api/helpers'
import { createMedia } from '../../../api/media'
import { patchSociety, societyQueries } from '../../../api/societies'
import { i18nSocietyType } from '../../../i18n'
import {
  completeGuide,
  CONSTANTS_SOCIETY_GUIDES,
  societyGuidesQueries,
} from '../../../api/society-guides'
import { StartGettingStarted } from './StartGettingStarted'
import { useDocumentTitle } from '../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../navigation/reverseDocumentTitle'
import { useAuthenticatedUserId } from '../../../hooks/useAuthenticatedUserId'
import { isUserAdminInSociety } from '../../../helpers/society'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'

const PageDescription = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  return (
    <div>
      <h2 style={{ ...theme.textVariants.h2, marginTop: theme.spacing.m }}>
        {translate('gettingStartedScreen.title')}
      </h2>

      <p style={{ ...theme.textVariants.base, marginBottom: theme.spacing.m }}>
        {translate('gettingStartedScreen.description', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
      </p>
    </div>
  )
}

const TodosCompleted = ({
  onPress,
  isLoading,
}: {
  onPress: () => void
  isLoading: boolean
}): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  return (
    <div
      style={{ marginBottom: theme.spacing.l }}
      className="rounded-md bg-brandLightBrown border border-brandBrown p-5"
    >
      <h2 style={{ ...theme.textVariants.h2 }}>
        {translate('gettingStartedScreen.todosCompleted.title')}
      </h2>

      <p style={{ ...theme.textVariants.base }}>
        {translate('gettingStartedScreen.todosCompleted.description', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
      </p>
      <Button
        onClick={onPress}
        loading={isLoading}
        style={{ marginTop: theme.spacing.s }}
        variant={ButtonVariant.PRIMARY}
        label={translate('gettingStartedScreen.todosCompleted.button')}
      />
    </div>
  )
}

export const GettingStartedView = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const navigate = useNavigate()
  const { id: societyId } = useParams()
  const { data: society } = useQuery(societyQueries.getSociety(societyId))
  const queryClient = useQueryClient()
  const { setToastNotification } = useToastNotifications()

  const authUserId = useAuthenticatedUserId()
  const currentUserIsAdmin = isUserAdminInSociety(society, authUserId as string)
  const { data: guide, isLoading: isLoadingGuide } = useQuery(
    societyGuidesQueries.gettingStarted(societyId, currentUserIsAdmin)
  )

  useDocumentTitle(reverseDocumentTitle('management:getting-started'))

  const { mutate: mutateCompleted, isPending: isPendingCompleted } =
    useMutation({
      mutationFn: async () => {
        completeGuide({
          societyId: societyId!,
          completed: true,
          guideId: CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GETTING_STARTED,
        })
        await queryClient.invalidateQueries({
          queryKey: societyGuidesQueries.gettingStarted(societyId).queryKey,
        })
        navigate(
          reverseUrl('management:getting-started-completed', { id: societyId })
        )
      },
    })

  const { mutate: mutateUncompleted, isPending: isUncompletedPending } =
    useMutation({
      mutationFn: async () => {
        await completeGuide({
          completed: false,
          societyId: societyId!,
          guideId: CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GETTING_STARTED,
        })

        await queryClient.invalidateQueries({
          queryKey: societyGuidesQueries.gettingStarted(societyId).queryKey,
        })
      },
    })

  const { mutate: mutateUploadImage, isPending: isUploadImagePending } =
    useMutation({
      mutationFn: async ({ e }: { e: React.ChangeEvent<HTMLInputElement> }) => {
        if (!e.target.files) {
          return
        }
        if (!societyId) {
          throw new Error('Society ID is undefined')
        }
        const images = Array.from(e.target.files)
        if (images.length === 0) {
          return
        }
        const image = images[0]
        if (!isImageType(image.type)) {
          setToastNotification(
            ToastType.DANGER,
            translate('uploadImage.invalidImageType')
          )
          return
        }
        await new Promise((resolve, reject) => {
          // eslint-disable-next-line no-new
          new Compressor(image, {
            quality: 0.8,
            success: async (result) => {
              const base64 = await convertBase64(result)
              const media = await createMedia('image', base64 as string, true)
              const mediaId = media.data.data?._id
              if (!mediaId) {
                throw new Error('Media ID is undefined')
              }
              await patchSociety({
                id: societyId,
                body: { coverPhotoId: mediaId },
              })
              resolve(true)
            },
            error: (err) => {
              reject(err)
            },
          })
        })
      },
      onError: (e) => {
        captureException(e)
        setToastNotification(
          ToastType.DANGER,
          translate('flashMessage.somethingWentWrongError')
        )
      },
    })
  const isLoading = isLoadingGuide

  if (isLoading) {
    return <SpinnerWrapper />
  }

  if (!guide?.started) {
    return <StartGettingStarted societyId={societyId!} />
  }
  if (guide?.completed) {
    return (
      <ErrorView
        title=""
        subtitle={translate('gettingStartedScreen.completedView.description', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        illustration={IllustrationChoices.CHECKLIST}
        buttons={[
          {
            id: 'getting-started-uncomplete-button',
            button: {
              label: translate('gettingStartedScreen.completedView.button'),
              variant: ButtonVariant.PRIMARY,
              loading: isUncompletedPending,
              onClick: () => {
                mutateUncompleted()
              },
            },
          },
        ]}
      />
    )
  }

  const todos: React.ReactNode[] | undefined = guide?.steps
    ?.map((gettingStarted, index) => {
      switch (gettingStarted.id) {
        case 'billing': {
          return (
            <Link
              key={gettingStarted.id}
              to={reverseUrl('management:billing-payment', { id: societyId })}
            >
              <Todo.Item
                index={index}
                title={translate('gettingStartedScreen.todos.billing.title')}
                description={translate(
                  'gettingStartedScreen.todos.billing.description'
                )}
                done={gettingStarted.completed}
                icon={IconChoices.PAYMENT}
              />
            </Link>
          )
        }
        case 'society_image': {
          return (
            <label key={gettingStarted.id} className="cursor-pointer">
              <Todo.Item
                index={index}
                title={translate(
                  'gettingStartedScreen.todos.society_image.title',
                  { context: i18nSocietyType(society?.societyTypeDisplayName) }
                )}
                description={translate(
                  'gettingStartedScreen.todos.society_image.description',
                  { context: i18nSocietyType(society?.societyTypeDisplayName) }
                )}
                isLoading={isUploadImagePending}
                done={gettingStarted.completed}
                icon={IconChoices.NEW_MESSAGE}
              />
              <input
                type="file"
                accept="image/png, image/jpg, image/jpeg"
                className="hidden"
                disabled={isUploadImagePending}
                onChange={(e) => {
                  mutateUploadImage({ e })
                }}
              />
            </label>
          )
        }
        case 'register': {
          return (
            <Link
              key={gettingStarted.id}
              to={reverseUrl('management:register-entrance', { id: societyId })}
            >
              <Todo.Item
                index={index}
                title={translate('gettingStartedScreen.todos.register.title', {
                  context: i18nSocietyType(society?.societyTypeDisplayName),
                })}
                description={translate(
                  'gettingStartedScreen.todos.register.description',
                  { context: i18nSocietyType(society?.societyTypeDisplayName) }
                )}
                done={gettingStarted.completed}
                icon={IconChoices.HOUSEHOLD}
              />
            </Link>
          )
        }
        case 'functions': {
          return (
            <Link
              key={gettingStarted.id}
              to={reverseUrl('management:function-settings', { id: societyId })}
            >
              <Todo.Item
                index={index}
                title={translate('gettingStartedScreen.todos.functions.title', {
                  context: i18nSocietyType(society?.societyTypeDisplayName),
                })}
                description={translate(
                  'gettingStartedScreen.todos.functions.description',
                  {
                    context: i18nSocietyType(society?.societyTypeDisplayName),
                  }
                )}
                done={gettingStarted.completed}
                icon={IconChoices.ICON_TOGGLE}
              />
            </Link>
          )
        }
        case 'members': {
          return (
            <Link
              key={gettingStarted.id}
              to={reverseUrl('management:register-member', { id: societyId })}
            >
              <Todo.Item
                index={index}
                title={translate('gettingStartedScreen.todos.members.title', {
                  context: i18nSocietyType(society?.societyTypeDisplayName),
                })}
                description={translate(
                  'gettingStartedScreen.todos.members.description',
                  { context: i18nSocietyType(society?.societyTypeDisplayName) }
                )}
                done={gettingStarted.completed}
                icon={IconChoices.BOARD}
              />
            </Link>
          )
        }
        default: {
          captureException(
            `GettingStartedView: Unknown getting started id ${gettingStarted.id}`
          )
          return <></>
        }
      }
    })
    .flatMap((todo) => (todo ? [todo] : []))

  if (!todos) {
    // TODO: Add translations
    return (
      <ErrorView
        illustration={IllustrationChoices.EMPTY}
        title={translate('gettingStartedScreen.error.title')}
        subtitle={translate('gettingStartedScreen.error.subtitle')}
      />
    )
  }

  const stepsDone = guide?.steps?.filter((t) => t.completed).length || 0
  const total = guide?.steps?.length || 0

  return (
    <div
      className="max-w-2xl"
      style={{
        backgroundColor: theme.colors.white,
      }}
    >
      {total === stepsDone && (
        <TodosCompleted
          isLoading={isPendingCompleted}
          onPress={() => mutateCompleted()}
        />
      )}
      <PageDescription />
      <Todo.Progress stepsDone={stepsDone} totalSteps={total} />
      <div style={{ marginTop: theme.spacing.m }}>{todos}</div>
    </div>
  )
}
